import type { ReactElement } from 'react';
import React, { Component } from 'react';
import type { CarouselSlideProps } from './CarouselSlide';
import CarouselButton from './CarouselButton';
import { Button } from 'venn-ui-kit';
import { SpecialCssClasses } from 'venn-utils';
import { cn } from '@/lib/utils';

export interface CarouselProps {
  /**
   * Callback fired when selection is changed.
   * @param index the index now selected.
   */
  onSelectionChanged?: (index: number, trigger?: string) => void;
  /**
   * If true, Carousel will determine the selected item based solely on props.
   */
  useExternalState?: boolean;
  selectedItem?: number;
  children: ReactElement<CarouselSlideProps>[];
}

interface CarouselState {
  selectedItem: number; // 0-based index of currently-selected item in the carousel
}

export default class Carousel extends Component<CarouselProps, CarouselState> {
  state = {
    selectedItem: 0,
  };

  static getDerivedStateFromProps(nextProps: CarouselProps, prevState: CarouselState) {
    if (nextProps.useExternalState && nextProps.selectedItem !== prevState.selectedItem) {
      return { selectedItem: nextProps.selectedItem };
    }
    return null;
  }

  selectSlide = (index: number, trigger?: string) => {
    // check that the selection has actually changed and within [0, # of children)
    if (index !== this.state.selectedItem && index >= 0 && index < React.Children.count(this.props.children)) {
      !this.props.useExternalState && this.setState({ selectedItem: index });
      if (this.props.onSelectionChanged) {
        this.props.onSelectionChanged(index, trigger);
      }
    }
  };

  handleBackClick = (): void => {
    this.selectSlide(this.state.selectedItem - 1, 'button');
  };

  handleNextClick = (): void => {
    this.selectSlide(this.state.selectedItem + 1, 'button');
  };

  handleCarouselButtonClick = (index: number): void => {
    this.selectSlide(index, 'bubble');
  };

  componentDidUpdate() {
    const carouselLength = React.Children.count(this.props.children);
    if (this.state.selectedItem >= carouselLength) {
      this.selectSlide(carouselLength - 1);
    }
  }

  render() {
    const { children } = this.props;
    const { selectedItem } = this.state;
    const childCount = React.Children.count(children);
    const childArray = React.Children.toArray(children) as ReactElement<CarouselSlideProps>[];
    const selectedChild = childArray[selectedItem];
    return (
      <div>
        <div
          className="qa-carousel-container w-full bg-venn-grey-20 h-[60px] flex relative items-center"
          data-testid="carousel"
        >
          <span className="absolute text-venn-grey-60 ml-[30px] tracking-[1px] font-bold uppercase">
            {childCount > 0 && selectedChild ? selectedChild.props.description : null}
          </span>
          {childCount > 1 && (
            <div className="m-auto">
              {childArray.map((child, index: number) => (
                <CarouselButton
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  selected={index === selectedItem}
                  onClick={() => this.handleCarouselButtonClick(index)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="w-[60%] m-auto pt-[50px] pb-10">{selectedItem >= 0 ? selectedChild : null}</div>

        {childCount > 1 && (
          <div className={cn('w-full flex relative pb-[30px]', SpecialCssClasses.NotDownloadable)}>
            <div className="qa-carousel-buttons m-auto">
              <Button disabled={selectedItem === 0} onClick={this.handleBackClick}>
                Back
              </Button>
              <Button disabled={selectedItem === childCount - 1} onClick={this.handleNextClick}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}
